<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    :disabled="status.isLoading"
    loading-message="Loading Call Summary..."
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      Call Summary
    </template>

    <template #card-notes>
      <div class="card-notes">
        <p>
          This will get any generated summary for the call. If a generated summary isn't found, none will be returned. If you want to
          manually generate a summary for a call, please use the form "Generate Call Summary". That summary will be saved and can be
          retrieved using this form.
        </p>
      </div>
    </template>

    <template #content>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Call Log ID* </label>
        <div class="col-sm-7">
          <sanitized-input
            id="callLogId"
            v-model="callLogId"
            placeholder="Call Log ID"
            required
          />
        </div>
      </div>
      <div
        v-if="status.summaries && status.summaries.length > 0"
        class="mt-4"
      >
        <div
          v-for="summary in status.summaries"
          :key="summary.id"
          class="summary-item mb-3"
        >
          <div
            class="summary-header d-flex justify-content-between align-items-center"
            @click="toggleSummary(summary.id)"
          >
            <div class="d-flex justify-content-between w-100">
              <strong>{{ summary.agent?.name }}</strong>
              <span>
                <strong>{{ new Date(summary.createdAt).toLocaleString() }}</strong>
              </span>
            </div>
            &nbsp;&nbsp;&nbsp;&nbsp;
            <i :class="[expandedSummaries.includes(summary.id) ? 'fa fa-arrow-up' : 'fa fa-arrow-down']" />
          </div>
          <div
            v-show="expandedSummaries.includes(summary.id)"
            class="summary-content"
          >
            <pre class="summary-text">{{ summary.summary }}</pre>
          </div>
        </div>
      </div>
      <div
        v-else-if="status.error"
        class="mt-4"
      >
        <div class="alert alert-danger">
          {{ status.error }}
        </div>
      </div>
    </template>

    <template #submit-button>
      Get Call Summary
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';

import util from '@/Utils/util';
import { CallSummary } from '@/models/callLog';
import { callLogService } from '@/services/callLogs';

const pageId: string = 'call-summary';

const callLogId = ref<string>('');
const expandedSummaries = ref<string[]>([]);

const status = reactive({
  isLoading: false,
  summaries: [] as CallSummary[],
  error: '',
});

const toggleSummary = (id: string): void => {
  const index = expandedSummaries.value.indexOf(id);
  if (index === -1) {
    expandedSummaries.value.push(id);
  } else {
    expandedSummaries.value.splice(index, 1);
  }
};

const submitForm = async (): Promise<void> => {
  status.summaries = [];
  status.error = '';
  status.isLoading = true;

  try {
    const res = await callLogService.getCallSummariesByCallLogId(parseInt(callLogId.value));

    if (res && res.length > 0) {
      status.summaries = res;
      // Expand the most recent summary by default
      expandedSummaries.value = res[0]?.id ? [res[0].id] : [];
    } else {
      status.error = `There are no summaries for Call Log ${callLogId.value}`;
    }
  } catch (error) {
    console.log(error);
    status.error = util.getGraphqlError(error, false);
  } finally {
    status.isLoading = false;
  }
};

const resetForm = (): void => {
  status.summaries = [];
  status.error = '';
  callLogId.value = '';
  expandedSummaries.value = [];
};

onMounted(async () => {
  const route = useRoute();
  callLogId.value = (route.query.callSummary as string) ?? '';

  if (callLogId.value) {
    await submitForm();
    util.moveScrollComponent(`#${pageId}`);
  }
});
</script>

<style lang="scss">
#call-summary {
  .summary-item {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    overflow: hidden;
  }

  .summary-header {
    padding: 0.75rem 1rem;
    background-color: #f8f9fa;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #e9ecef;
    }
  }

  .summary-content {
    padding: 0;
  }

  .summary-text {
    text-align: left;
    white-space: pre-wrap;
    font-family: inherit;
    margin: 0;
    padding: 1rem;
    background-color: #fff;
  }
}
</style>
